import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CaretLeft, CaretRight, ImageSquare, Trash, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const ORDER = ["Standard", "Deluxe", "Suite", "Family Suite", "Dormitory"];
const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const photoUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/room-types/photos/${p.id}/file`;

export const RoomTypePhotos = ({ rooms }) => {
  const types = useMemo(() => {
    const rank = (t) => (ORDER.indexOf(t) === -1 ? 99 : ORDER.indexOf(t));
    return [...new Set(rooms.map((r) => r.type))].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [rooms]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [viewer, setViewer] = useState(null); // { type, index }
  const inputs = useRef({});

  const load = () => api.get("/room-types/photos").then(({ data }) => setPhotos(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const byType = (t) => photos.filter((p) => p.room_type === t);
  const roomCount = (t) => rooms.filter((r) => r.type === t).length;

  const upload = async (type, file) => {
    if (!file) return;
    setUploading(type);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/room-types/${encodeURIComponent(type)}/photos`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Photo added to ${type}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(null);
      if (inputs.current[type]) inputs.current[type].value = "";
    }
  };

  const remove = async (photo) => {
    try {
      await api.delete(`/room-types/photos/${photo.id}`);
      toast.success("Photo removed");
      setViewer(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (types.length === 0) return null;
  const viewerPhotos = viewer ? byType(viewer.type) : [];
  const current = viewer ? viewerPhotos[viewer.index] : null;

  return (
    <div className="space-y-3" data-testid="room-types-section">
      <div>
        <h2 className="font-heading text-xl font-bold">Room types</h2>
        <p className="text-xs text-muted-foreground">Photos per room type — shown to your team here only; EasyStay manages its own listing photos.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {types.map((t) => {
          const tp = byType(t);
          const cover = tp[0];
          return (
            <div key={t} className="border border-border bg-card" data-testid={`room-type-card-${slug(t)}`}>
              <button type="button" disabled={!cover} onClick={() => setViewer({ type: t, index: 0 })}
                className="relative block h-32 w-full overflow-hidden bg-muted/40" data-testid={`room-type-cover-${slug(t)}`}>
                {cover ? (
                  <img src={photoUrl(cover)} alt={`${t} rooms`} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground/50">
                    <ImageSquare size={28} />
                  </div>
                )}
                <span className="absolute bottom-1.5 right-1.5 bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white"
                  data-testid={`room-type-photo-count-${slug(t)}`}>
                  {tp.length}/8 photos
                </span>
              </button>
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <span className="font-heading text-sm font-bold">{t}</span>
                  <span className="text-xs text-muted-foreground">{roomCount(t)} room{roomCount(t) === 1 ? "" : "s"}</span>
                </div>
                {tp.length > 1 && (
                  <div className="mt-2 flex gap-1.5 overflow-x-auto">
                    {tp.slice(1).map((p) => (
                      <div key={p.id} className="group relative h-10 w-14 shrink-0 overflow-hidden" data-testid={`room-type-thumb-${p.id}`}>
                        <img src={photoUrl(p)} alt="" className="h-full w-full cursor-pointer object-cover"
                          onClick={() => setViewer({ type: t, index: tp.findIndex((x) => x.id === p.id) })} />
                        <button type="button" onClick={() => remove(p)} data-testid={`room-type-photo-delete-${p.id}`}
                          className="absolute inset-0 hidden items-center justify-center bg-black/55 text-white group-hover:flex">
                          <Trash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={(el) => (inputs.current[t] = el)} type="file" accept="image/jpeg,image/png,image/webp"
                  className="hidden" data-testid={`room-type-file-${slug(t)}`} onChange={(e) => upload(t, e.target.files?.[0])} />
                <Button variant="outline" size="sm" className="mt-2.5 w-full transition-colors hover:border-primary"
                  disabled={uploading === t || tp.length >= 8} onClick={() => inputs.current[t]?.click()}
                  data-testid={`room-type-upload-${slug(t)}`}>
                  <UploadSimple size={14} className="mr-1.5" />
                  {uploading === t ? "Uploading…" : tp.length >= 8 ? "Photo limit reached" : "Upload photo"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-3xl p-2" data-testid="room-photo-viewer">
          <DialogTitle className="sr-only">Room type photo</DialogTitle>
          {current && (
            <div className="space-y-2">
              <img src={photoUrl(current)} alt={`${viewer.type} photo ${viewer.index + 1}`}
                className="max-h-[70vh] w-full object-contain" data-testid="viewer-image" />
              <div className="flex items-center justify-between px-2 pb-1">
                <span className="text-xs text-muted-foreground">
                  {viewer.type} — {viewer.index + 1} of {viewerPhotos.length}
                </span>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" disabled={viewerPhotos.length < 2} data-testid="viewer-prev"
                    onClick={() => setViewer({ ...viewer, index: (viewer.index - 1 + viewerPhotos.length) % viewerPhotos.length })}>
                    <CaretLeft size={14} />
                  </Button>
                  <Button variant="outline" size="sm" disabled={viewerPhotos.length < 2} data-testid="viewer-next"
                    onClick={() => setViewer({ ...viewer, index: (viewer.index + 1) % viewerPhotos.length })}>
                    <CaretRight size={14} />
                  </Button>
                  <Button variant="outline" size="sm" data-testid="viewer-delete"
                    className="hover:border-destructive hover:text-destructive" onClick={() => remove(current)}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
